import type { IMapbackTrackingItem, JourneyData, JourneyPoint, JourneySegment } from '~/utils/interface/mapback';
import { calculateDurationSeconds } from './formatters';

type JsonObject = Record<string, unknown>;

function asObject(value: unknown): JsonObject | null {
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return value as JsonObject;
  }
  return null;
}

function pickFirstString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
}

function toNumber(value: unknown, fallback = 0): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toTimestamp(value: unknown): string {
  if (typeof value !== 'string') return '';
  return value.trim();
}

function toStatus(value: unknown): 0 | 1 | 2 | 3 {
  const parsed = toNumber(value, 0);
  if (parsed === 1 || parsed === 2 || parsed === 3) return parsed;
  return 0;
}

/** Chỉ đọc các key meta cần cho tên/IMEI, không giữ reference tới object thô. */
function readPointIdentityFromRaw(raw: unknown): {
  full_name?: string;
  person_name?: string;
  device_name?: string;
  device_imei?: string;
} {
  const o = asObject(raw);
  if (!o) return {};
  return {
    full_name: typeof o.full_name === 'string' ? o.full_name : undefined,
    person_name: typeof o.person_name === 'string' ? o.person_name : undefined,
    device_name: typeof o.device_name === 'string' ? o.device_name : undefined,
    device_imei: typeof o.device_imei === 'string' ? o.device_imei : undefined,
  };
}

function extractSegmentsFromPayload(payload: unknown): JsonObject[] {
  if (typeof payload === 'string') {
    try {
      const parsed = JSON.parse(payload) as unknown;
      return extractSegmentsFromPayload(parsed);
    } catch {
      return [];
    }
  }

  if (Array.isArray(payload)) {
    return payload.filter((item): item is JsonObject => asObject(item) !== null) as JsonObject[];
  }

  const root = asObject(payload);
  if (!root) return [];

  if (Array.isArray(root.data)) {
    return root.data.filter((item): item is JsonObject => asObject(item) !== null) as JsonObject[];
  }

  const result = root.result;
  if (Array.isArray(result)) {
    return result.filter((item): item is JsonObject => asObject(item) !== null) as JsonObject[];
  }
  const resultObj = asObject(result);
  if (resultObj && Array.isArray(resultObj.data)) {
    return resultObj.data.filter((item): item is JsonObject => asObject(item) !== null) as JsonObject[];
  }

  const values = Object.values(root);
  if (
    values.length > 0 &&
    values.every((item) => {
      const o = asObject(item);
      return o !== null && typeof o.start_time !== 'undefined' && typeof o.end_time !== 'undefined';
    })
  ) {
    return values as JsonObject[];
  }

  return [];
}

function extractPlaybackMeta(payload: unknown): JsonObject {
  const root = asObject(payload) ?? {};
  const result = asObject(root.result) ?? {};
  const data = asObject(root.data) ?? {};

  return {
    person_id: root.person_id ?? result.person_id ?? data.person_id,
    full_name:
      root.full_name ??
      result.full_name ??
      data.full_name ??
      root.person_name ??
      result.person_name ??
      data.person_name ??
      root.device_name ??
      result.device_name ??
      data.device_name,
    device_imei: root.device_imei ?? result.device_imei ?? data.device_imei,
  };
}

function cleanPoint(
  raw: JsonObject,
  index: number,
  segmentIndex: number,
  segmentType: JourneySegment['type'],
  segmentDuration: number
): JourneyPoint {
  const timestamp =
    toTimestamp(raw.timestamp) ||
    toTimestamp(raw.trktime) ||
    toTimestamp(raw.tracktime) ||
    toTimestamp(raw.updatetime) ||
    toTimestamp(raw.time);

  return {
    id: toNumber(raw.id, index),
    timestamp,
    latitude: toNumber(raw.latitude ?? raw.lat),
    longitude: toNumber(raw.longitude ?? raw.lng),
    speed: toNumber(raw.speed),
    address:
      typeof raw.address === 'string' ? raw.address : typeof raw.full_address === 'string' ? raw.full_address : '',
    battery_percent: toNumber(raw.battery_percent ?? raw.pin),
    status: toStatus(raw.status),
    segmentIndex,
    segmentType,
    segmentDuration,
  };
}

function cleanSegment(raw: JsonObject, index: number): JourneySegment {
  const rawType = typeof raw.type === 'string' ? raw.type : 'stop';
  const type: JourneySegment['type'] =
    rawType === 'route' || rawType === 'lost_gps' || rawType === 'lost_gprs' ? rawType : 'stop';

  const startTime = toTimestamp(raw.start_time);
  const endTime = toTimestamp(raw.end_time);
  const segmentDuration = calculateDurationSeconds(startTime, endTime);

  const rawPoints = Array.isArray(raw.points) ? raw.points : [];
  const points: JourneyPoint[] = rawPoints.map((pt, ptIdx) => {
    const obj = asObject(pt);
    if (!obj) {
      return cleanPoint({}, ptIdx, index, type, segmentDuration);
    }
    return cleanPoint(obj, ptIdx, index, type, segmentDuration);
  });

  return {
    type,
    start_time: startTime,
    end_time: endTime,
    start_address: typeof raw.start_address === 'string' ? raw.start_address : '',
    end_address: typeof raw.end_address === 'string' ? raw.end_address : '',
    total_distance: toNumber(raw.total_distance),
    status_duration:
      raw.status_duration == null || raw.status_duration === '' ? '00:00:00' : String(raw.status_duration),
    points,
  };
}

export interface ToCleanJourneyDataOptions {
  personId: number;
  matchedTracking: IMapbackTrackingItem | null;
}
export function toCleanJourneyData(responsePayload: unknown, options: ToCleanJourneyDataOptions): JourneyData {
  const { personId, matchedTracking } = options;

  const rawSegments = extractSegmentsFromPayload(responsePayload);
  const segments: JourneySegment[] = rawSegments.map((seg, idx) => cleanSegment(seg, idx));

  const allPoints: JourneyPoint[] = segments
    .flatMap((s) => s.points)
    .filter((p) => Number.isFinite(p.latitude) && Number.isFinite(p.longitude));

  const meta = extractPlaybackMeta(responsePayload);
  const firstSegment = rawSegments[0];
  const firstPointRaw = Array.isArray(firstSegment?.points) ? firstSegment.points[0] : undefined;
  const idFromFirstPoint = readPointIdentityFromRaw(firstPointRaw);

  const fullNameFromPayload = pickFirstString(
    matchedTracking?.full_name,
    matchedTracking?.device_name,
    meta.full_name,
    idFromFirstPoint.full_name,
    idFromFirstPoint.person_name,
    idFromFirstPoint.device_name
  );
  const imeiFromPayload = pickFirstString(matchedTracking?.device_imei, meta.device_imei, idFromFirstPoint.device_imei);

  const rootPayload = asObject(responsePayload) ?? {};

  return {
    person_id: toNumber(matchedTracking?.person_id ?? meta.person_id ?? rootPayload.person_id, personId),
    full_name: fullNameFromPayload,
    device_imei: imeiFromPayload,
    segments,
    allPoints,
  };
}
